<template>
  <div class="container py-5 mt-5">
    <!-- Chargement -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="mt-3">Chargement de l'offre...</p>
    </div>

    <!-- Token invalide / offre expirée -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-warning d-inline-block">
        <i class="fas fa-exclamation-triangle fa-2x mb-3 d-block"></i>
        <h4>Lien invalide</h4>
        <p>{{ error }}</p>
        <router-link to="/" class="btn btn-primary mt-2">
          <i class="fas fa-home"></i> Retour à l'accueil
        </router-link>
      </div>
    </div>

    <!-- Déjà postulé -->
    <div v-else-if="hasApplied" class="text-center py-5">
      <div class="alert alert-info d-inline-block">
        <i class="fas fa-check-circle fa-2x mb-3 d-block"></i>
        <h4>Candidature existante</h4>
        <p>Vous avez déjà postulé à cette offre.</p>
        <router-link to="/status" class="btn btn-primary mt-2">
          <i class="fas fa-tachometer-alt"></i> Voir mon tableau de bord
        </router-link>
      </div>
    </div>

    <!-- Non connecté -->
    <div v-else-if="!isLoggedIn" class="invitation-content">
      <div class="offer-header mb-4">
        <h1 class="offer-title">{{ jobOffer.title }}</h1>
        <div class="offer-meta">
          <span class="badge bg-warning text-dark">Offre sur invitation</span>
          <span class="deadline-info">
            <i class="fas fa-calendar-alt"></i>
            Date limite: {{ formatDate(jobOffer.deadline) }}
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-8">
          <div class="card mb-4">
            <div class="card-header">
              <h3><i class="fas fa-info-circle text-primary"></i> Description du poste</h3>
            </div>
            <div class="card-body">
              <div v-html="jobOffer.description"></div>
            </div>
          </div>
          <div class="card mb-4">
            <div class="card-header">
              <h3><i class="fas fa-list-check text-primary"></i> Prérequis</h3>
            </div>
            <div class="card-body">
              <div v-html="jobOffer.requirements"></div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="card">
            <div class="card-header">
              <h4><i class="fas fa-user-plus text-primary"></i> Postuler</h4>
            </div>
            <div class="card-body text-center">
              <p>Pour postuler à cette offre, veuillez vous connecter ou créer un compte.</p>
              <router-link
                :to="{ name: 'Login', query: { redirect: `/invite/${token}` } }"
                class="btn btn-primary w-100 mb-2"
              >
                <i class="fas fa-sign-in-alt"></i> Se connecter
              </router-link>
              <router-link
                :to="{ name: 'Register', query: { redirect: `/invite/${token}` } }"
                class="btn btn-outline-primary w-100"
              >
                <i class="fas fa-user-plus"></i> S'inscrire
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Connecté : inscription automatique en cours -->
    <div v-else class="invitation-content">
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
        <p class="mt-3">Préparation de vos tests...</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvitationLanding',

  data() {
    return {
      token: this.$route.params.token,
      jobOffer: null,
      hasApplied: false,
      loading: true,
      error: null
    }
  },

  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token')
    }
  },

  async mounted() {
    await this.loadInvitation()
  },

  methods: {
    async loadInvitation() {
      try {
        const response = await this.$http.get(`/invite/${this.token}`)
        this.jobOffer = response.data.job_offer || response.data.data?.job_offer
        this.hasApplied = response.data.has_applied || response.data.data?.has_applied || false
        this.loading = false

        // Si connecté et pas encore postulé, candidature automatique
        if (this.isLoggedIn && !this.hasApplied && this.jobOffer) {
          await this.autoApply()
        }
      } catch (err) {
        if (err.response?.status === 404) {
          this.error = "Ce lien d'invitation est invalide ou l'offre a expiré."
        } else {
          this.error = "Une erreur est survenue lors du chargement de l'offre."
        }
        this.loading = false
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async autoApply() {
      try {
        const response = await this.$http.post(`/invite/${this.token}/apply`)

        const application = response.data.application || response.data.data?.application

        if (application?.id) {
          this.$router.push(`/applications/${application.id}/tests`)
        } else {
          this.$router.push('/status')
        }
      } catch (err) {
        const message = err.response?.data?.message || err.response?.data?.data?.message || "Une erreur est survenue."
        this.error = message
      }
    }
  }
}
</script>

<style scoped>
.invitation-content {
  max-width: 1200px;
  margin: 0 auto;
}

.offer-header {
  background: linear-gradient(135deg, #29b3cd 0%, #bc6a3c 100%);
  color: white;
  padding: 2rem;
  border-radius: 10px;
}

.offer-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.offer-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.deadline-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .offer-header {
    padding: 1.5rem;
  }
  .offer-title {
    font-size: 1.8rem;
  }
}
</style>
